import React, { useEffect, useState } from 'react';
import { Box, Text, Select, Spinner, HStack, IconButton, Collapse } from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { updateUserSettings } from './api/userSettings';
import { useUserSettingsQuery, useUserSettingsOptionsQuery } from './query/useUserSettings';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOnboardingChecklist } from './onboarding/OnboardingChecklistProvider';

function LabeledSelect({ label, value, options, onChange, disabled }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 12, opacity: 0.8 }}>{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)} isDisabled={disabled}>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </Select>
    </label>
  );
}

export default function UserSettingsCard() {
  const [open, setOpen] = useState(true);
  const [retryPolicy, setRetryPolicy] = useState('');
  const [personality, setPersonality] = useState('');
  const [daytimePreference, setDaytimePreference] = useState('');
  const settingsQuery = useUserSettingsQuery();
  const optionsQuery = useUserSettingsOptionsQuery();
  const qc = useQueryClient();
  const { refresh: refreshChecklist } = useOnboardingChecklist();
  const fallbackOptions = { retryPolicy: [], personality: [], daytimePreference: [] };
  const opts = optionsQuery.data ?? fallbackOptions;

  const updateMutation = useMutation({
    mutationFn: updateUserSettings,
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ['userSettings'] });
      const previous = qc.getQueryData(['userSettings']);
      qc.setQueryData(['userSettings'], (old = {}) => ({ ...old, ...patch }));
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['userSettings'], ctx.previous);
      }
    },
    onSuccess: () => {
      refreshChecklist();
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['userSettings'] });
    },
  });

  useEffect(() => {
    if (!settingsQuery.data) return;
    setRetryPolicy(settingsQuery.data.retryPolicy || '');
    setPersonality(settingsQuery.data.personality || '');
    setDaytimePreference(settingsQuery.data.daytimePreference || '');
  }, [settingsQuery.data]);

  async function pushUpdate(patch) {
    try {
      await updateMutation.mutateAsync(patch);
    } catch (err) {
      console.error('Failed to update user settings', err);
    }
  }

  const loading = settingsQuery.isLoading || optionsQuery.isLoading;
  const saving = updateMutation.isPending;
  const disabled = loading || saving;

  return (
    <Box id="user-settings-card" borderWidth="1px" borderRadius="xl" p={0} mb={4} bg="bg.surface" borderColor="border.subtle">
      <HStack p={3} justifyContent="space-between">
        <HStack>
          <IconButton aria-label={open ? 'Collapse' : 'Expand'} icon={open ? <FiChevronDown /> : <FiChevronRight />} size="sm" onClick={() => setOpen((v) => !v)} />
          <Text fontSize="lg" fontWeight="bold">Personalization</Text>
        </HStack>
        {saving ? <Text fontSize="sm" opacity={0.7}>Saving…</Text> : null}
      </HStack>

      <Collapse in={open} animateOpacity>
        <Box p={3} borderTopWidth="1px">
          <Text mt={0} opacity={0.8}>Settings Ekkos uses when planning reminders.</Text>

          {loading ? <Spinner mt={4} /> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
              <LabeledSelect
                label="Retry mode"
                value={retryPolicy}
                options={opts.retryPolicy}
                disabled={disabled}
                onChange={(val) => {
                  setRetryPolicy(val);
                  pushUpdate({ retryPolicy: val });
                }}
              />
              <LabeledSelect
                label="Personality"
                value={personality}
                options={opts.personality}
                disabled={disabled}
                onChange={(val) => {
                  setPersonality(val);
                  pushUpdate({ personality: val });
                }}
              />
              <LabeledSelect
                label="Daytime preference"
                value={daytimePreference}
                options={opts.daytimePreference}
                disabled={disabled}
                onChange={(val) => {
                  setDaytimePreference(val);
                  pushUpdate({ daytimePreference: val });
                }}
              />
            </div>
          )}

        </Box>
      </Collapse>
    </Box>
  );
}
